import { useQuery } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { accountStore } from '@/services/auth/store';
import type { Account } from '@/services/db/account/entity';
import { getAccountQueryOptions } from '@/services/db/account/queries';

/**
 * The signed-in account's id, or null before the auth stores are populated.
 *
 * Read from the store rather than through `useAuth`, which throws on empty
 * stores: a send test can be rendered inside a managed modal, which mounts
 * outside the auth provider, and its consumers' tests render it without
 * seeding auth at all.
 */
function useAccountId(): number | null {
  return useStore(accountStore)?.id ?? null;
}

/**
 * Whether the account still has to confirm its email address before it can send
 * anything, test messages included.
 *
 * Shared, because both send tests ask the same question and an answer that
 * differed between them would be a bug on one of the two screens.
 */
export function useNeedsEmailConfirmation(): boolean {
  const accountId = useAccountId();
  const accountRecord = useQuery({
    ...getAccountQueryOptions(accountId ?? 0),
    enabled: accountId != null,
  }).data;

  return isUnconfirmed(accountRecord);
}

/**
 * Nothing is known while the account read is in flight, and holding a send
 * hostage to it would be worse than letting a rare one fail.
 */
function isUnconfirmed(accountRecord: Account | undefined): boolean {
  return accountRecord != null && accountRecord.confirmed !== true;
}
